'use client';

/**
 * SocialIntelRail — horizontal avatar rail of TRACKED accounts on the chain
 * overview (pass-1 activity-first layout, polish-backlog bet B).
 *
 * Ordered by 24h activity: entities with a fresh AI activity pulse lead the rail
 * (carrying their sentiment dot + one-line pulse), then the remaining tracked
 * accounts by coverage. Swipeable overflow-x rail on mobile.
 *
 * Client component (SSR'd — the /tags anchors stay crawlable): the page builds
 * the (serializable) items from already-fetched members + pulse rows. Client
 * because the pass-2 polish hid the native x-scrollbar (`.scrollbar-hide` +
 * right-edge fade), which removed the ONLY mouse affordance for reaching the
 * overflow cards on desktop — so we map a vertical wheel over the rail to
 * horizontal scroll (native non-passive listener; React's delegated onWheel is
 * passive so preventDefault would be ignored). Touch swipe is untouched, and
 * the wheel passes through to the page once the rail hits either end.
 * Honest empty state when no accounts are tracked yet.
 */
import { useEffect, useRef } from 'react';
import { CARD, CARD_HOVER } from '@/components/dashboard/format';
import { EntityProfileLink } from '@/components/dashboard/EntityPreviewDrawer';

export interface SocialIntelItem {
  slug: string;
  name: string;
  image?: string;
  initials: string;
  handle?: string;
  /** Present when the entity has a fresh activity pulse (leads the rail). */
  pulse?: {
    text: string;
    sentiment: 'bullish' | 'bearish' | 'neutral' | null;
  };
}

function sentimentDotClass(s: 'bullish' | 'bearish' | 'neutral' | null): string {
  return s === 'bullish'
    ? 'bg-signal-green-text'
    : s === 'bearish'
      ? 'bg-alert-red-text'
      : 'bg-slate-ds';
}

export default function SocialIntelRail({
  chainLabel,
  items,
}: {
  chainLabel: string;
  items: SocialIntelItem[];
}) {
  const railRef = useRef<HTMLDivElement>(null);

  // Desktop traversal: with the scrollbar hidden, a mouse user has no native way
  // to advance the rail — translate a dominant-vertical wheel into horizontal
  // scroll while the rail can still move; at either end the event is left alone
  // so the page keeps scrolling (no scroll-jacking).
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const onWheel = (ev: WheelEvent) => {
      if (Math.abs(ev.deltaY) <= Math.abs(ev.deltaX)) return; // real horizontal input scrolls natively
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return; // no overflow — nothing to traverse
      // deltaMode 1 = lines (Firefox wheel) — normalize to ~pixels.
      const delta = ev.deltaMode === 1 ? ev.deltaY * 16 : ev.deltaY;
      const atEnd = delta > 0 ? el.scrollLeft >= max - 1 : el.scrollLeft <= 0;
      if (atEnd) return;
      ev.preventDefault();
      el.scrollLeft = Math.max(0, Math.min(max, el.scrollLeft + delta));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  if (items.length === 0) {
    return (
      <p className="font-mono text-[11px] text-parchment-dim">
        No tracked {chainLabel} accounts yet — live activity feeds land as coverage grows.
      </p>
    );
  }

  return (
    // Swipe affordance: the native x-scrollbar is hidden (`.scrollbar-hide`, the shared
    // globals.css utility — scrollability kept) and a right-edge fade into the page's
    // `ink` background signals the overflow instead (same pattern as the ticker rails).
    <div className="relative">
      <div ref={railRef} className="flex gap-2.5 overflow-x-auto pb-1.5 scrollbar-hide">
        {items.map((e) => (
          // In-shell profile wiring (bet F): plain left-click opens the entity drawer
          // in place; the crawlable /tags href + modified clicks are preserved.
          <EntityProfileLink
            key={e.slug}
            slug={e.slug}
            className={`group w-[156px] flex-shrink-0 ${CARD} ${CARD_HOVER} p-3.5`}
          >
            <div className="flex flex-col items-start gap-2">
              {e.image ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={e.image} alt="" loading="lazy" className="h-10 w-10 rounded-full object-cover" />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-full border-[0.5px] border-divider bg-surface2 font-mono text-[12px] text-parchment-dim">
                  {e.initials}
                </span>
              )}
              <div className="min-w-0 w-full">
                <div className="truncate font-display text-[12.5px] font-bold text-parchment group-hover:text-scout-gold transition-colors">
                  {e.name}
                </div>
                {e.handle && (
                  <div className="truncate font-mono text-[10px] text-parchment-dim">
                    @{e.handle.replace(/^@/, '')}
                  </div>
                )}
              </div>
              {e.pulse ? (
                <div className="flex items-start gap-1.5">
                  <span
                    className={`mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full ${sentimentDotClass(e.pulse.sentiment)}`}
                    aria-hidden
                  />
                  <p className="font-display text-[11px] leading-snug text-parchment-dim line-clamp-2">
                    {e.pulse.text}
                  </p>
                </div>
              ) : (
                <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.1em] text-parchment-dim">
                  <span className="h-1.5 w-1.5 rounded-full bg-signal-green" aria-hidden /> tracked
                </span>
              )}
            </div>
          </EntityProfileLink>
        ))}
      </div>
      {/* Right-edge fade — subtle "more cards" affordance (non-interactive). */}
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-ink to-transparent"
        aria-hidden
      />
    </div>
  );
}
